import React, { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import BroadcastPageHeader from './BroadcastPageHeader'
import BroadcastUnavailable from './BroadcastUnavailable'
import ExposureHeadline from './ExposureHeadline'
import ExposureTimeline from './ExposureTimeline'
import ExposureValueTable from './ExposureValueTable'
import ExposurePlacement from './ExposurePlacement'
import ExposureMethod from './ExposureMethod'
import useBroadcastExposure from './useBroadcastExposure'
import { useBroadcastOrigin } from './broadcastOrigin'
import { currencyOf, buildRows, rateFor, totalsOf, splitBySponsorship, rank } from '../broadcastValuation'

/**
 * What one broadcast was worth, for the rightsholder working in the app.
 *
 * READS TOP DOWN AS AN ARGUMENT, not as a dashboard. What the broadcast was
 * worth; why area-seconds rank brands differently from time; the per-brand
 * numbers; where on the venue the value came from; and how it was all measured.
 * Someone should be able to stop after the first screen and have the answer.
 *
 * NOT THE THING A SPONSOR RECEIVES. That is the published report: a separate,
 * frozen artefact generated from the same figures and sent as a link. This page
 * is live, and re-aggregation moving a number here is correct rather than
 * alarming — which is exactly why the two cannot be one surface. It is also
 * where a rightsholder curates (fix a zone, exclude a bad detection, merge a
 * name variant) BEFORE publishing, so editorial controls belong on it.
 *
 * It reuses the same exposure payload as its siblings, so nothing new is
 * fetched and the three views cannot disagree about who counts.
 */
const BroadcastExposurePage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { state: originState } = useBroadcastOrigin()
  const { customerId, data, loading, locked } = useBroadcastExposure(id)

  const model = useMemo(() => {
    if (!data) return null
    const rate = rateFor(data.broadcast)
    const rows = buildRows(data.brands, rate)
    /**
     * THIS PAGE IS SPONSORS ONLY. Every table, chart and total below shows
     * inventory somebody paid for; competitors and incidental brands are a
     * different conversation and live on the Brands page.
     *
     * The one thing that still needs the whole population is the SHARE, and
     * it has to be computed before the filter — a share of sponsors over
     * sponsors is 100% and says nothing.
     */
    const { sponsors } = splitBySponsorship(rows)
    const countedArea = rows.reduce((a, r) => a + r.areaSeconds, 0)
    const sponsoredArea = sponsors.reduce((a, r) => a + r.areaSeconds, 0)
    // Null rather than 0 when nothing counts: "no sponsored share" and
    // "nothing to take a share of" are different statements, and only one of
    // them is about sponsors.
    const sponsorShare = countedArea > 0 ? sponsoredArea / countedArea : null
    // Re-ranked over the sponsors alone. buildRows ranks the whole detected
    // field, which on a broadcast with two sponsors among twenty brands
    // numbered them 1 and 14 — a rank against rows the page does not show.
    const ranked = rank(sponsors)
    return { rate, rows: ranked, totals: totalsOf(ranked, rate), sponsorShare }
  }, [data])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    )
  }

  if (!data || !model) return <BroadcastUnavailable locked={locked} />

  const { broadcast, zones, coverage, frames, timeline } = data
  const { rate, rows, totals, sponsorShare } = model

  // Null when frames were never recorded — a broadcast analysed before
  // broadcast_frames existed. Zero would claim a sponsor was never on
  // screen, which is a measurement nobody made.
  const presence = frames && frames.analysed > 0
    ? frames.withSponsored / frames.analysed
    : null
  const currency = currencyOf(broadcast)

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <BroadcastPageHeader broadcast={broadcast} view="exposure" />

        <ExposureHeadline
          broadcast={broadcast} totals={totals} rate={rate}
          sponsorShare={sponsorShare} presence={presence}
        />

        {/* WHO got what, before WHEN they got it. The table is the answer
            to the question the page is opened with — a sponsor looking for
            their own line — and the timeline explains the shape of it. */}
        <ExposureValueTable
          rows={rows}
          totals={totals}
          currency={currency}
          priced={rate !== null}
          onSelect={(key) => navigate(`/broadcasts/${broadcast.id}/brands/${encodeURIComponent(key)}`, { state: originState })}
        />

        {timeline && (
          <ExposureTimeline
            broadcast={broadcast} timeline={timeline} customerId={customerId}
          />
        )}

        <ExposurePlacement
          broadcast={broadcast} zones={zones} rate={rate} currency={currency}
        />

        <ExposureMethod
          broadcast={broadcast} coverage={coverage} frames={frames}
          rate={rate} currency={currency}
        />
      </div>
    </div>
  )
}

export default BroadcastExposurePage
